// Shared changelog renderer used by the /changelog page and the in-app
// "What's new" modal, so the tag palette, date grouping and grouped layout
// live in ONE place.

import { formatDate } from '../lib/date';

// Re-exported because the changelog page and the What's new modal both
// reach for it through this module.
export { formatDate };

export const TAG_STYLES = {
    feature: 'bg-primary/10 text-primary',
    fix: 'bg-rose-50 text-rose-600',
    outage: 'bg-red-50 text-red-700',
    update: 'bg-amber-50 text-amber-700',
};

// The full-page changelog adds a coloured border to each tag; the compact modal
// does not. Kept alongside TAG_STYLES so the two stay in sync.
const TAG_BORDERS = {
    feature: 'border border-primary/20',
    fix: 'border border-rose-200',
    outage: 'border border-red-200',
    update: 'border border-amber-200',
};

// Strip a leading conventional-commit prefix (feat:, fix:, chore(scope):, feat!:,
// …) from the message, since the tag is already shown as its own label. Only a
// recognised type is stripped, so a description that legitimately starts with a
// word + colon (e.g. "iOS: …") is left alone.
const COMMIT_PREFIX = /^(feat|feature|fix|chore|refactor|docs|style|perf|test|build|ci|revert|update)(\([^)]*\))?!?:\s*/i;
export function stripCommitPrefix(message) {
    return (message || '').replace(COMMIT_PREFIX, '');
}

// Group entries (already sorted newest-first) into ordered date groups so the
// date is shown once as a header instead of on every row.
export function groupByDate(entries) {
    const groups = [];
    let current = null;
    for (const entry of entries) {
        const date = formatDate(entry.commit_date);
        if (!current || current.date !== date) {
            current = { date, items: [] };
            groups.push(current);
        }
        current.items.push(entry);
    }
    return groups;
}

const VARIANTS = {
    page: { outer: 'space-y-8', headerMargin: 'mb-3', inner: 'space-y-3', border: true },
    modal: { outer: 'space-y-4', inner: 'space-y-2.5', border: false },
};

/**
 * The page is full width, so it stacks the row (date beside the tag, message
 * below) only on mobile and goes side-by-side from sm up. The modal is narrow
 * at EVERY screen size — the dialog has a fixed max width — so it always uses
 * that stacked layout rather than a viewport breakpoint that would never match.
 */
export default function ChangelogList({ entries, variant = 'page' }) {
    const v = VARIANTS[variant] || VARIANTS.page;
    const isPage = variant === 'page';
    return (
        <div className={v.outer}>
            {groupByDate(entries).map(group => (
                <div key={group.date}>
                    {/* The stacked layout repeats the date beside each tag, so the group
                        header is only for the page's wide (sm+) layout. */}
                    {isPage && (
                        <h4 className={`hidden sm:block text-xs font-semibold uppercase tracking-wider text-stone-400 ${v.headerMargin}`}>
                            {group.date}
                        </h4>
                    )}
                    <div className={v.inner}>
                        {group.items.map(entry => (
                            <div key={entry.id} className={isPage ? 'flex flex-col gap-1 sm:flex-row sm:items-start sm:gap-2' : 'flex flex-col gap-1'}>
                                <div className="flex items-center gap-2">
                                    <span className={`text-xs font-semibold px-2 py-0.5 rounded-full capitalize shrink-0 w-16 text-center ${TAG_STYLES[entry.tag] || TAG_STYLES.update}${v.border ? ` ${TAG_BORDERS[entry.tag] || TAG_BORDERS.update}` : ''}`}>
                                        {entry.tag}
                                    </span>
                                    <span className={`text-xs text-stone-400${isPage ? ' sm:hidden' : ''}`}>{group.date}</span>
                                </div>
                                <p className="text-sm text-foreground leading-snug min-w-0">{stripCommitPrefix(entry.message)}</p>
                            </div>
                        ))}
                    </div>
                </div>
            ))}
        </div>
    );
}
